/**
 * API client for the feat-289 crew call-out review queue.
 *
 * Backend: `restaurant-backend/routes/admin_crew_callouts.py`. The wire contract
 * is `CONTRACT-ADMIN.md` in the feature root — written and owned by the backend
 * slice — and every shape, default and status code below is from it. If
 * something here disagrees with that file, that file wins.
 *
 * ALL FOUR ROUTES ARE `Depends(require_admin)` — Admin only, and cross-tenant by
 * design (there is no tenant parameter and none is needed; the gate is the whole
 * control, CONTRACT-ADMIN.md §1). The SPA-side half of that boundary is
 * `<AdminRoute>` on `/crew-callouts`, asserted in
 * `components/auth/__tests__/feat289CrewCalloutsGuard.test.tsx` (AC24).
 *
 * SECURITY: `line_en`, `line_es`, `source_title`, and (feat-322) `basis` and
 * `hold_reasons` are UNTRUSTED — the lines and `basis` are LLM-written,
 * `source_title` is copied verbatim out of a transcript summary, and
 * `hold_reasons` echo model output in their failure text. Consumers MUST
 * render them as plain text (`{value}` JSX nodes or a
 * controlled input value) and never through `dangerouslySetInnerHTML`,
 * `innerHTML`, a markdown-to-HTML pass, or attribute interpolation.
 */

import { config } from '../config/environment';
import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';
import { fakeCalloutWeek, fakeCalloutWeeks } from './fakeCrewCalloutData';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

// ============================================================
// Types — CONTRACT-ADMIN.md §2
// ============================================================

export type CalloutStatus = 'draft' | 'approved' | 'rejected';

/**
 * feat-322 — which step of the job's fallback chain produced the row's source.
 * `null` (or the field absent) on a pre-feat-322 row. Treat any other string
 * the same as `null`: no badge, no crash.
 */
export type CalloutSourceKind = 'highlight' | 'highlight_fallback' | 'transcript';

/** One proposed weekly praise line. Mirrors `CalloutRow` exactly. */
export interface CalloutRow {
  id: string;
  server_id: string;
  /** Joined at read time — the row has no name column. */
  first_name: string;
  location_id: string | null;
  /** Build the location filter from these; there is no locations endpoint (§5). */
  location_name: string | null;
  week_start: string;
  /** The day the line names. Null on a held row. */
  event_date: string | null;
  source_shift_id: string | null;
  source_interaction_id: string | null;
  /** VERBATIM, from `summary_json.highlights`. Untrusted — plain text only. */
  source_title: string | null;
  /** Untrusted — plain text only. NULL ON A HELD DRAFT, see `isHeldRow`. */
  line_en: string | null;
  /** Untrusted — plain text only. */
  line_es: string | null;
  status: CalloutStatus;
  /**
   * feat-322. Optional AND nullable: absent on an older backend, null on a
   * legacy row. Typed `string` beyond the known kinds so an unknown value from
   * a newer backend still type-checks and renders as "no badge".
   */
  source_kind?: CalloutSourceKind | string | null;
  /**
   * feat-322. A short PARAPHRASED description of the moment (no quotes), set on
   * transcript-sourced rows and on held rows (best basis found). Untrusted LLM
   * text — plain text only.
   */
  basis?: string | null;
  /**
   * feat-322. Per-step failure reasons on a held row, in the order recorded.
   * Untrusted derived text — plain text only.
   */
  hold_reasons?: string[] | null;
  /** The reviewer's email (AC23). */
  reviewed_by: string | null;
  /** ISO, UTC. */
  reviewed_at: string | null;
  edited: boolean;
  created: string | null;
  /** Stamped on the first phone read. */
  delivered_at: string | null;
}

/** `GET /api/admin/crew-callouts` — one week. */
export interface CalloutWeek {
  /** Location-local Monday. */
  week_start: string;
  /** Mon–Fri, five days (D8). */
  week_end: string;
  location_id: string | null;
  draft_count: number;
  /**
   * Servers who WORKED that week (≥ 1 non-deleted shift) and got no row at all
   * — they see the Benny fallback card. **Not derivable from `rows`**
   * (CONTRACT-ADMIN.md §2): a queue of six good lines looks identical whether
   * the other forty servers had nothing that qualified or the job stopped after
   * six. Render it where the reviewer cannot miss it.
   */
  no_candidate_count: number;
  /** Drafts first, per the backend's ordering. */
  rows: CalloutRow[];
}

/** `GET /api/admin/crew-callouts/weeks` — one entry per reviewable week. */
export interface CalloutWeekSummary {
  week_start: string;
  drafts: number;
  approved: number;
  rejected: number;
}

// ============================================================
// Derived predicates — one definition, used by the view and its tests
// ============================================================

/**
 * A **held** draft: every step of the job's fallback chain failed (feat-322:
 * best highlight, next highlights, transcript), so the row landed `draft`
 * carrying only its best source (`source_title` / `basis`) and `hold_reasons`
 * for a human to write the line from (D10/AC31). Never a silent fallback,
 * never `approved`.
 *
 * The queue therefore has two kinds of draft and they are different jobs — check
 * a machine-written line, or write one. A held row rendered as an empty line
 * looks like a bug and gets reported as one, which is why this is a named
 * predicate rather than an inline `!row.line_en` at each call site.
 *
 * Keyed on `line_en` because the contract keys it there ("`line_en === null`");
 * a row missing only the Spanish half is not a held row, it is a bad row, and
 * the approve endpoint refuses to create one (400 on a one-sided edit).
 */
export function isHeldRow(row: CalloutRow): boolean {
  return row.status === 'draft' && (row.line_en === null || row.line_en === '');
}

/** True when both halves are present and non-blank — the approve precondition. */
export function hasBothLines(lineEn: string, lineEs: string): boolean {
  return lineEn.trim().length > 0 && lineEs.trim().length > 0;
}

/**
 * `week_start` must be a Monday — the backend answers a non-Monday with `400`,
 * deliberately, because an empty queue is indistinguishable from a week nobody
 * qualified for and a typo would then read as a result (CONTRACT-ADMIN.md §1).
 * Checked here too so a mis-typed URL never becomes a network round trip that
 * renders as an error banner.
 *
 * Parsed as a UTC calendar date, NOT `new Date("2026-09-14")` in local time:
 * the string is a location-local Monday, and re-interpreting it in the browser's
 * zone west of UTC would slide it to Sunday.
 */
export function isMonday(weekStart: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekStart)) return false;
  const [y, m, d] = weekStart.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return false;
  // Round-trip guard: Date.UTC(2026, 1, 30) silently becomes March 2nd.
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) {
    return false;
  }
  return dt.getUTCDay() === 1;
}

// ============================================================
// API
// ============================================================

export interface ListCalloutsFilters {
  /** `YYYY-MM-DD`, must be a Monday. Omit for the week awaiting review. */
  weekStart?: string;
  /** Omit for all locations. */
  locationId?: string;
}

/**
 * One week of the queue. `week_start` defaults server-side to
 * `last_completed_week()` in `America/Los_Angeles` — the same expression the
 * weekly job used to WRITE the rows, so the queue's default week and the week
 * the job built cannot drift apart. Do not compute a default here.
 */
export async function listCallouts(
  filters: ListCalloutsFilters = {}
): Promise<CalloutWeek> {
  // `start:fake` must work with no backend (CLAUDE.md). Inert today —
  // `useDummyData` is false in all four environments — see the header of
  // `fakeCrewCalloutData.ts` for why it is written and kept accurate anyway.
  if (config.useDummyData) {
    return fakeCalloutWeek(filters.weekStart, filters.locationId);
  }

  const params = new URLSearchParams();
  if (filters.weekStart) params.set('week_start', filters.weekStart);
  if (filters.locationId) params.set('location_id', filters.locationId);
  const qs = params.toString();

  const data = await fetchApi<CalloutWeek>(
    `/api/admin/crew-callouts${qs ? `?${qs}` : ''}`,
    { headers: getAuthHeaders() }
  );
  return { ...data, rows: data.rows ?? [] };
}

/** The week picker. Newest first; `limit` 1–52, default 8. */
export async function listCalloutWeeks(limit = 8): Promise<CalloutWeekSummary[]> {
  if (config.useDummyData) return fakeCalloutWeeks(limit);

  const data = await fetchApi<CalloutWeekSummary[]>(
    `/api/admin/crew-callouts/weeks?limit=${encodeURIComponent(String(limit))}`,
    { headers: getAuthHeaders() }
  );
  return data ?? [];
}

/**
 * Approve a row, optionally editing both lines first.
 *
 * - No `edit` → approve the machine's own lines unchanged; `edited` stays false.
 * - `edit` with both lines → edit-then-approve; both written, `edited` true.
 *
 * **Both or neither.** The backend answers a one-sided edit with `400`, and a
 * blank or whitespace-only half with `400` — a row with an empty Spanish half
 * renders an English card to a Spanish-preferred server, which is AC19's failure
 * wearing a success's clothes. The UI enforces the same rule before sending so
 * the reviewer sees it as a disabled button, not as a server error.
 */
export async function approveCallout(
  id: string,
  edit?: { line_en: string; line_es: string }
): Promise<CalloutRow> {
  return fetchApi<CalloutRow>(
    `/api/admin/crew-callouts/${encodeURIComponent(id)}/approve`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      ...(edit ? { body: edit } : {}),
    }
  );
}

/**
 * Reject a row. No body and **no reason field** — §5 rules one out, and a
 * required reason turns a two-second decision into a form.
 *
 * A rejected row's source is blocked for the cross-week dedup window like any
 * other prior row (D15/AC14), so the same moment is not re-proposed next Monday.
 * That happens in the job's query — there is nothing to build here for it.
 */
export async function rejectCallout(id: string): Promise<CalloutRow> {
  return fetchApi<CalloutRow>(
    `/api/admin/crew-callouts/${encodeURIComponent(id)}/reject`,
    { method: 'POST', headers: getAuthHeaders() }
  );
}
